import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import RichTextEditor from '../../components/RichTextEditor';
import FormTranslationsTab from '../../components/forms/FormTranslationsTab';
import PaperLayoutTab from '../../components/forms/PaperLayoutTab';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import api, { formsApi } from '../../lib/api';
import FormulaPreview from '../../components/forms/FormulaPreview';
import FormulaHelper from '../../components/forms/FormulaHelper';
import FillBlankField from '../../components/forms/FillBlankField';
import PatientFieldRefPicker from '../../components/forms/PatientFieldRefPicker';
import { formsApi as _formsApiForRef } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Save, Plus, Trash2, GripVertical, ArrowLeft, Settings,
  Type, Hash, Calendar, Mail, Phone, List, CheckSquare, Circle,
  AlignLeft, Upload, Link2, ArrowUp, ArrowDown, Loader2, Info,
  Columns, History, PenTool, SeparatorHorizontal, Table2, Eye,
  Heart, LayoutGrid, Clock, Copy, Activity, Move
} from 'lucide-react';

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'select', label: 'Dropdown', icon: List },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { value: 'radio', label: 'Radio', icon: Circle },
  { value: 'textarea', label: 'Text Area', icon: AlignLeft },
  { value: 'file', label: 'File Upload', icon: Upload },
  { value: 'form_reference', label: 'Form Reference', icon: Link2 },
  { value: 'info', label: 'Info / Instructions', icon: Info },
  { value: 'signature', label: 'Signature', icon: PenTool },
  { value: 'section', label: 'Section Break', icon: SeparatorHorizontal },
  { value: 'checkbox_table', label: 'Checkbox Table', icon: Table2 },
  { value: 'multi_column', label: 'Multi-Column', icon: LayoutGrid },
  { value: 'checkbox_text', label: 'Checkbox + Text', icon: CheckSquare },
  { value: 'fill_blank', label: 'Fill-in-the-Blank', icon: AlignLeft },
  { value: 'reminder', label: 'Reminder', icon: Clock },
  { value: 'calculated', label: 'Calculated', icon: Hash },
  { value: 'calc_table', label: 'Calc Table', icon: Table2 },
];

const CHILD_FIELD_TYPES = ['text', 'number', 'date', 'email', 'phone', 'select', 'checkbox', 'checkbox_text', 'radio', 'textarea', 'info'];

const PRESETS = [
  { label: 'Blood Pressure', icon: Heart, fields: [
    { label: 'Systolic BP (mmHg)', field_type: 'number', placeholder: '120', width: 'half', required: true },
    { label: 'Diastolic BP (mmHg)', field_type: 'number', placeholder: '80', width: 'half', required: true },
  ]},
  { label: 'Vital Signs Set', icon: Heart, fields: [
    { label: 'Systolic BP', field_type: 'number', placeholder: '120', width: 'half' },
    { label: 'Diastolic BP', field_type: 'number', placeholder: '80', width: 'half' },
    { label: 'Heart Rate (bpm)', field_type: 'number', placeholder: '72', width: 'third' },
    { label: 'Temperature (F)', field_type: 'number', placeholder: '98.6', width: 'third' },
    { label: 'SpO2 (%)', field_type: 'number', placeholder: '98', width: 'third' },
  ]},
  { label: 'Patient Name', icon: Type, fields: [
    { label: 'First Name', field_type: 'text', width: 'half', required: true, patient_field_ref: 'first_name' },
    { label: 'Last Name', field_type: 'text', width: 'half', required: true, patient_field_ref: 'last_name' },
  ]},
  { label: 'Contact Info', icon: Phone, fields: [
    { label: 'Email', field_type: 'email', width: 'half', patient_field_ref: 'email' },
    { label: 'Phone', field_type: 'phone', width: 'half', patient_field_ref: 'phone' },
  ]},
  { label: 'Medical Diagnosis', icon: Activity, fields: [
    { label: 'Diagnosis', field_type: 'textarea', width: 'full', patient_field_ref: 'medical_diagnosis' },
  ]},
];
const ROLES = ['physician', 'nurse', 'billing_specialist', 'patient', 'practice_admin'];
const ROLE_LABELS = { physician: 'Physician', nurse: 'Nurse', billing_specialist: 'Billing', patient: 'Patient', practice_admin: 'Practice Admin' };
const CATEGORIES = ['intake', 'assessment', 'consent', 'survey', 'clinical', 'administrative', 'billing', 'referral'];
const WIDTH_OPTIONS = [
  { value: 'full', label: 'Full Width' },
  { value: 'half', label: 'Half Width' },
  { value: 'third', label: 'One Third' },
];

export default function FormBuilderPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const editId = searchParams.get('id');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('draft');
  const [updateIntervalDays, setUpdateIntervalDays] = useState('');
  const [assignedRoles, setAssignedRoles] = useState([]);
  const [fields, setFields] = useState([]);
  const [pdfLayoutMeta, setPdfLayoutMeta] = useState(null);  // {print_layout, source_pdf_pages, page_dimensions} for PDF-imported forms
  const [paperLayout, setPaperLayout] = useState(null);  // {enabled, page_size, orientation, custom_*, snap, grid} for the drag-drop designer
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allForms, setAllForms] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [autoSaveId, setAutoSaveId] = useState(editId);
  const [lastSaved, setLastSaved] = useState(null);
  const [addAsPatientTab, setAddAsPatientTab] = useState(false);
  const [tabAlreadyLinked, setTabAlreadyLinked] = useState(false);
  const autoSaveTimer = React.useRef(null);
  const [activeTab, setActiveTab] = useState('fields');
  const [printTemplate, setPrintTemplate] = useState({
    page_size: 'letter', orientation: 'portrait',
    margins: { top: 15, right: 15, bottom: 15, left: 15 },
    header: { text: '', show_logo: true, alignment: 'center', font_size: 16, show_org_name: true, show_org_address: false },
    patient_info: { show: true, columns: 4, fields: ['name','dob','mrn','gender','phone','email','insurance','emergency_contact'] },
    sections: [],
    footer: { text: '', show_page_numbers: true, font_size: 8 }
  });
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiProgress, setAiProgress] = useState(null); // {message, total, completed}

  useEffect(() => {
    formsApi.list().then(res => setAllForms(res.data.forms || [])).catch(() => {});
    const aiImportId = searchParams.get('aiImportId');
    if (editId) {
      loadForm(editId);
    } else if (location.state?.aiForm) {
      loadAiForm(location.state.aiForm);
    } else if (aiImportId) {
      // Coming from a notification link — fetch the persisted import result
      formsApi.getAiImport(aiImportId).then(res => {
        const imp = res.data;
        if (imp.status !== 'done' || !imp.result) {
          toast.error(imp.error || 'This import is not ready yet.');
          return;
        }
        const r = imp.result;
        const gen = r.generated_form || r.form_definition;
        if (!gen) { toast.error('Import has no extracted form'); return; }
        loadAiForm(gen);
        if (r.print_template) setPrintTemplate(prev => ({ ...prev, ...r.print_template }));
      }).catch(() => toast.error('Could not load AI import'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const loadAiForm = (ai) => {
    setName(ai.name || '');
    setDescription(ai.description || '');
    setCategory(ai.category || '');
    setUpdateIntervalDays(ai.update_interval_days || '');
    setFields((ai.fields || []).map((f, i) => ({ ...f, field_id: f.field_id || f.id || `field_${i}`, label: f.label || '', field_type: f.field_type || f.type || 'text', order: i, width: f.width || 'full', page_index: f.page_index, bbox: f.bbox })));
    if (ai.print_layout === 'pdf_original') {
      setPdfLayoutMeta({
        print_layout: 'pdf_original',
        source_pdf_pages: ai.source_pdf_pages || [],
        page_dimensions: ai.page_dimensions || [],
      });
    }
    toast.success(ai.print_layout === 'pdf_original'
      ? `Loaded "${ai.name}" with ${ai.source_pdf_pages?.length || 0} PDF page(s). Review fields and save — printing will preserve the original layout.`
      : `AI-generated form "${ai.name || ''}" loaded. Review and save.`);
  };

  const loadForm = async (id) => {
    setLoading(true);
    try {
      const res = await formsApi.get(id);
      const f = res.data;
      setName(f.name); setDescription(f.description || ''); setCategory(f.category || '');
      // Normalize legacy/unknown statuses — backend now restricts to draft/published/archived.
      const validStatus = ['draft', 'published', 'archived'].includes(f.status) ? f.status : 'draft';
      setStatus(validStatus); setAssignedRoles(f.assigned_roles || []);
      setUpdateIntervalDays(f.update_interval_days || '');
      setFields((f.fields || []).map(fld => ({ ...fld, width: fld.width || 'full' })));
      if (f.print_layout === 'pdf_original') {
        setPdfLayoutMeta({
          print_layout: 'pdf_original',
          source_pdf_pages: f.source_pdf_pages || [],
          page_dimensions: f.page_dimensions || [],
        });
      }
      if (f.paper_layout) setPaperLayout(f.paper_layout);
    } catch (err) { toast.error('Failed to load form'); }
    finally { setLoading(false); }
    // Check whether this form is already linked as a patient tab
    try {
      const tabsRes = await api.get('/patient-tabs');
      const linked = (tabsRes.data?.tabs || []).some(t => t.type === 'form_linked' && t.form_id === id);
      if (linked) { setAddAsPatientTab(true); setTabAlreadyLinked(true); }
    } catch (e) { /* noop */ }
  };

  const loadAuditTrail = async () => {
    const fid = editId || autoSaveId;
    if (!fid) return;
    setLoadingAudit(true);
    try {
      const res = await formsApi.auditTrail(editId || autoSaveId);
      setAuditEntries(res.data.audit_trail || []);
    } catch { toast.error('Failed to load audit trail'); }
    finally { setLoadingAudit(false); }
  };

  const addField = (type) => {
    const newField = {
      field_id: `field_${Date.now()}`,
      label: type === 'info' ? 'Information' : type === 'section' ? 'Section Title' : type === 'signature' ? 'Signature' : type === 'checkbox_table' ? 'Checklist Table' : type === 'multi_column' ? 'Column Group' : type === 'checkbox_text' ? '' : type === 'reminder' ? 'Follow-up Reminder' : type === 'calculated' ? 'Calculated Value' : type === 'calc_table' ? 'Calculation Table' : type === 'fill_blank' ? 'Fill-in-the-Blanks' : '',
      field_type: type,
      required: false,
      placeholder: '',
      options: type === 'select' || type === 'radio' || type === 'checkbox' ? ['Option 1'] : type === 'checkbox_text' ? ['Option 1'] : null,
      reference_form_id: null,
      reference_field_id: null,
      order: fields.length,
      width: 'full',
      info_text: type === 'info' ? '' : null,
      table_columns: type === 'checkbox_table' ? ['Item', 'Yes', 'No', 'N/A'] : null,
      table_rows: type === 'checkbox_table' ? ['Row 1'] : null,
      patient_field_ref: null,
      column_count: type === 'multi_column' ? 2 : null,
      children: type === 'multi_column' ? [[], []] : null,
      reminder_days: type === 'reminder' ? 7 : null,
      reminder_label: type === 'reminder' ? '' : null,
      formula: type === 'calculated' ? '' : null,
      format: type === 'calculated'
        ? { type: 'number', decimals: 2 }
        : (type === 'calc_table' ? { type: 'number', decimals: 0 } : null),
      calc_table_config: type === 'calc_table' ? {
        columns: [
          { key: 'item', label: 'Item', type: 'text' },
          { key: 'qty', label: 'Qty', type: 'number' },
          { key: 'price', label: 'Price', type: 'number' },
          { key: 'total', label: 'Total', type: 'formula', formula: 'row.qty * row.price' },
        ],
        initial_rows: 3,
        allow_add_rows: true,
        totals: { enabled: true, cells: { total: { agg: 'sum' }, qty: { agg: 'sum' } } },
      } : null,
      fib_template: type === 'fill_blank' ? 'The patient\'s name is ___ born on ___ weighing ___ kg.' : null,
    };
    setFields([...fields, newField]);
  };

  const addPreset = (preset) => {
    const newFields = preset.fields.map((pf, i) => ({
      field_id: `field_${Date.now()}_${i}`,
      label: pf.label,
      field_type: pf.field_type,
      required: pf.required || false,
      placeholder: pf.placeholder || '',
      options: null,
      reference_form_id: null,
      reference_field_id: null,
      order: fields.length + i,
      width: pf.width || 'full',
      info_text: null,
      table_columns: null,
      table_rows: null,
      patient_field_ref: pf.patient_field_ref || null,
    }));
    setFields([...fields, ...newFields]);
    toast.success(`Added ${preset.label} (${newFields.length} fields)`);
  };

  const updateField = (index, updates) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], ...updates };
    setFields(updated);
  };

  const changeFieldType = (index, newType) => {
    const old = fields[index];
    if (old.field_type === newType) return;
    const needsOptions = ['select', 'radio', 'checkbox', 'checkbox_text'];
    const updates = { field_type: newType };
    // Add options if switching to a type that needs them
    if (needsOptions.includes(newType) && !needsOptions.includes(old.field_type)) {
      updates.options = ['Option 1'];
    }
    // Remove options if switching away from a type that had them
    if (!needsOptions.includes(newType) && needsOptions.includes(old.field_type)) {
      updates.options = null;
    }
    // Multi-column setup/teardown
    if (newType === 'multi_column' && old.field_type !== 'multi_column') {
      updates.column_count = 2;
      updates.children = [[], []];
    }
    if (newType !== 'multi_column' && old.field_type === 'multi_column') {
      updates.column_count = null;
      updates.children = null;
    }
    // Checkbox table setup/teardown
    if (newType === 'checkbox_table' && old.field_type !== 'checkbox_table') {
      updates.table_columns = ['Item', 'Yes', 'No', 'N/A'];
      updates.table_rows = ['Row 1'];
    }
    if (newType !== 'checkbox_table' && old.field_type === 'checkbox_table') {
      updates.table_columns = null;
      updates.table_rows = null;
    }
    // Info text
    if (newType === 'info') {
      updates.info_text = old.info_text || '';
    }
    if (newType !== 'info' && old.field_type === 'info') {
      updates.info_text = null;
    }
    // Form reference
    if (newType !== 'form_reference' && old.field_type === 'form_reference') {
      updates.reference_form_id = null;
      updates.reference_field_id = null;
    }
    // Reminder setup/teardown
    if (newType === 'reminder' && old.field_type !== 'reminder') {
      updates.reminder_days = 7;
      updates.reminder_label = '';
    }
    if (newType !== 'reminder' && old.field_type === 'reminder') {
      updates.reminder_days = null;
      updates.reminder_label = null;
    }
    updateField(index, updates);
  };

  const removeField = (index) => setFields(fields.filter((_, i) => i !== index));

  const duplicateField = (index) => {
    const src = fields[index];
    if (!src) return;
    // Deep clone via JSON to sever nested refs (children, options, calc_table_config, format)
    const clone = JSON.parse(JSON.stringify(src));
    clone.field_id = `field_${Date.now()}`;
    clone.label = `${src.label || 'Untitled'} (copy)`;
    // Regenerate child_ids so children of multi_column/checkbox_text don't collide
    if (Array.isArray(clone.children)) {
      clone.children = clone.children.map((col) =>
        Array.isArray(col) ? col.map((c, ci) => ({ ...c, child_id: `child_${Date.now()}_${ci}` })) : col
      );
    }
    const next = [...fields];
    next.splice(index + 1, 0, clone);
    setFields(next.map((f, i) => ({ ...f, order: i })));
    toast.success('Field duplicated');
  };

  const moveField = (index, dir) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= fields.length) return;
    const updated = [...fields];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setFields(updated.map((f, i) => ({ ...f, order: i })));
  };

  const addChildField = (fieldIdx, colIdx, childType) => {
    const updated = [...fields];
    const children = [...(updated[fieldIdx].children || [])];
    while (children.length <= colIdx) children.push([]);
    children[colIdx] = [...children[colIdx], {
      child_id: `child_${Date.now()}_${colIdx}`,
      label: '',
      field_type: childType,
      placeholder: '',
      options: (childType === 'checkbox' || childType === 'radio' || childType === 'select') ? ['Option 1'] : null,
      info_text: childType === 'info' ? '' : null,
    }];
    updated[fieldIdx] = { ...updated[fieldIdx], children };
    setFields(updated);
  };

  const updateChildField = (fieldIdx, colIdx, childIdx, updates) => {
    const updated = [...fields];
    const children = [...(updated[fieldIdx].children || [])];
    children[colIdx] = [...children[colIdx]];
    children[colIdx][childIdx] = { ...children[colIdx][childIdx], ...updates };
    updated[fieldIdx] = { ...updated[fieldIdx], children };
    setFields(updated);
  };

  const removeChildField = (fieldIdx, colIdx, childIdx) => {
    const updated = [...fields];
    const children = [...(updated[fieldIdx].children || [])];
    children[colIdx] = children[colIdx].filter((_, i) => i !== childIdx);
    updated[fieldIdx] = { ...updated[fieldIdx], children };
    setFields(updated);
  };

  const changeColumnCount = (fieldIdx, count) => {
    const updated = [...fields];
    const children = [...(updated[fieldIdx].children || [])];
    while (children.length < count) children.push([]);
    updated[fieldIdx] = { ...updated[fieldIdx], column_count: count, children: children.slice(0, count) };
    setFields(updated);
  };

  const toggleRole = (role) => {
    setAssignedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  const buildPayload = () => ({
    name: name || 'Untitled Form',
    description, category, status,
    update_interval_days: updateIntervalDays ? Number(updateIntervalDays) : 0,
    assigned_roles: assignedRoles,
    fields: fields.map((f, i) => ({
      ...f, order: i,
      table_rows: f.table_rows ? f.table_rows.filter(r => r.trim()) : f.table_rows,
      options: f.options ? f.options.filter(o => o.trim()) : f.options
    })),
    ...(pdfLayoutMeta || {}),
    ...(paperLayout ? { paper_layout: paperLayout } : {}),
  });

  // Auto-save with debounce
  useEffect(() => {
    if (fields.length === 0 && !name) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try {
        const payload = buildPayload();
        if (autoSaveId) {
          await formsApi.update(autoSaveId, payload);
        } else {
          if (!payload.name || payload.name === 'Untitled Form') return;
          const res = await formsApi.create(payload);
          setAutoSaveId(res.data.id);
          window.history.replaceState(null, '', `/forms/builder?id=${res.data.id}`);
        }
        setLastSaved(new Date());
      } catch (e) { /* silent auto-save failure */ }
    }, 2000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [name, description, category, status, assignedRoles, fields, paperLayout]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Form name is required'); return; }
    if (fields.length === 0) { toast.error('Add at least one field'); return; }
    for (const f of fields) {
      if (!f.label.trim() && f.field_type !== 'section' && f.field_type !== 'multi_column' && f.field_type !== 'field_reference') { toast.error(`A field needs a label`); return; }
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      let savedFormId = autoSaveId;
      if (autoSaveId) {
        await formsApi.update(autoSaveId, payload);
        toast.success('Form saved');
      } else {
        const res = await formsApi.create(payload);
        setAutoSaveId(res.data.id);
        savedFormId = res.data.id;
        toast.success('Form created');
      }
      // If user toggled "Add as patient tab", create/keep the tab link
      if (addAsPatientTab && !tabAlreadyLinked && savedFormId) {
        try {
          await api.post('/patient-tabs', {
            name: name.trim(),
            icon: 'FileText',
            type: 'form_linked',
            form_id: savedFormId,
          });
          setTabAlreadyLinked(true);
          toast.success('Added as patient tab');
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Could not link as patient tab');
        }
      }
      navigate('/forms');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
        : (typeof detail === 'string' ? detail : 'Failed to save');
      toast.error(msg);
    }
    finally { setSaving(false); }
  };

  const handleAiAnalyzeTemplate = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAiAnalyzing(true);
    setAiProgress({ message: 'Uploading…', total: null, completed: 0 });
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await formsApi.aiAnalyzeTemplate(formData, setAiProgress);
      const { form_definition, print_template } = res.data;
      if (form_definition) {
        if (form_definition.name) setName(form_definition.name);
        if (form_definition.fields?.length > 0) {
          setFields(form_definition.fields.map((f, i) => ({ ...f, field_id: f.field_id || `field_${Date.now()}_${i}`, order: i })));
        }
      }
      if (print_template) setPrintTemplate(prev => ({ ...prev, ...print_template }));
      toast.success('AI analyzed your form and created fields + print template!');
      setActiveTab('print');
    } catch (err) { toast.error(err.response?.data?.detail || 'AI analysis failed'); }
    finally { setAiAnalyzing(false); setAiProgress(null); }
  };

  const savePrintTemplate = async () => {
    const formId = autoSaveId || editId;
    if (!formId) { toast.error('Save the form first'); return; }
    try {
      await formsApi.savePrintTemplate(formId, printTemplate);
      toast.success('Print template saved');
    } catch { toast.error('Failed to save template'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="form-builder-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/forms')}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {editId || autoSaveId ? 'Edit Form' : 'New Form'}
            </h1>
            {lastSaved && <p className="text-[10px] text-[#94A3B8]">Auto-saved {lastSaved.toLocaleTimeString()}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          <label className="cursor-pointer">
            <Button variant="outline" className="flex items-center gap-2 border-[#0E7490] text-[#0E7490] hover:bg-[#F0FDFA]" disabled={aiAnalyzing} onClick={() => document.getElementById('ai-template-upload').click()} data-testid="ai-upload-pdf-btn" title="Upload a PDF form — AI extracts fields and preserves the exact print layout">
              {aiAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {aiAnalyzing
                ? (aiProgress?.total > 0
                    ? `Analyzing ${aiProgress.completed}/${aiProgress.total}…`
                    : 'Analyzing…')
                : 'Import PDF → preserves print layout'}
            </Button>
            {aiAnalyzing && aiProgress?.total > 0 && (
              <div className="mt-1 w-full h-1 bg-[#E2E8F0] rounded-full overflow-hidden" data-testid="ai-template-progress-bar">
                <div
                  className="h-full bg-[#0E7490] transition-all duration-500 ease-out"
                  style={{ width: `${Math.round((aiProgress.completed / aiProgress.total) * 100)}%` }}
                />
              </div>
            )}
            <input id="ai-template-upload" type="file" className="hidden" accept="image/*,.pdf" onChange={handleAiAnalyzeTemplate} />
          </label>
          {pdfLayoutMeta?.print_layout === 'pdf_original' && (
            <Button
              variant="outline"
              onClick={() => (editId || autoSaveId ? navigate(`/forms/${editId || autoSaveId}/layout-editor`) : toast.info('Save the form first, then adjust box positions'))}
              data-testid="form-layout-editor-btn"
            >
              <Move className="w-4 h-4 mr-2" />Adjust boxes
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowPreview(true)} data-testid="form-preview-btn">
            <Eye className="w-4 h-4 mr-2" />Preview
          </Button>
          {(editId || autoSaveId) && (
            <Button variant="outline" onClick={() => { setShowAudit(true); loadAuditTrail(); }} data-testid="form-audit-btn">
              <History className="w-4 h-4 mr-2" />Audit Trail
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="form-settings-btn"><Settings className="w-4 h-4 mr-2" />Settings</Button>
          <Button className="careiq-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-form-btn">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Form'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#E2E8F0]">
        <button onClick={() => setActiveTab('fields')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'fields' ? 'border-[#0E7490] text-[#0E7490]' : 'border-transparent text-[#64748B] hover:text-[#0A2342]'}`} data-testid="tab-fields">Form Fields</button>
        <button onClick={() => setActiveTab('paper')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'paper' ? 'border-[#0E7490] text-[#0E7490]' : 'border-transparent text-[#64748B] hover:text-[#0A2342]'}`} data-testid="tab-paper">Paper Layout</button>
        <button onClick={() => setActiveTab('print')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'print' ? 'border-[#0E7490] text-[#0E7490]' : 'border-transparent text-[#64748B] hover:text-[#0A2342]'}`} data-testid="tab-print">Print Layout</button>
        <button onClick={() => setActiveTab('translations')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'translations' ? 'border-[#0E7490] text-[#0E7490]' : 'border-transparent text-[#64748B] hover:text-[#0A2342]'}`} data-testid="tab-translations">Translations</button>
      </div>

      {activeTab === 'paper' && (
        <PaperLayoutTab
          fields={fields}
          setFields={setFields}
          paperLayout={paperLayout || { enabled: true, page_size: 'letter', orientation: 'portrait', custom_width: 816, custom_height: 1056, snap: true, grid: 8 }}
          setPaperLayout={setPaperLayout}
        />
      )}

      {activeTab === 'translations' && (
        <FormTranslationsTab
          formId={editId || autoSaveId}
          formName={name}
          formDescription={description}
          fields={fields}
        />
      )}

      {activeTab === 'fields' && (
      <div className="grid lg:grid-cols-4 gap-6">
        {/* Field Palette */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-4">
            {/* Convert to draggable form CTA */}
            {fields.length > 0 && !fields.some(f => f.canvas_position) && (
              <Card className="careiq-card border-l-4 border-l-[#0E7490] bg-[#F0FDFA] mb-3" data-testid="convert-to-draggable-card">
                <CardContent className="p-3">
                  <div className="text-xs font-semibold text-[#0A2342] mb-1">Want to drag fields around?</div>
                  <p className="text-[11px] text-[#64748B] mb-2">Convert this form into a paper canvas where you can drop fields exactly where you want them.</p>
                  <Button
                    size="sm"
                    className="w-full careiq-btn-primary text-xs h-7"
                    onClick={() => {
                      if (!paperLayout) {
                        setPaperLayout({ enabled: true, page_size: 'letter', orientation: 'portrait', custom_width: 816, custom_height: 1056, snap: true, grid: 8 });
                      }
                      setActiveTab('paper');
                      toast.success('Switch to Paper Layout — click "Auto-place unplaced fields" to start.');
                    }}
                    data-testid="convert-to-draggable-btn"
                  >
                    Convert to drag-droppable
                  </Button>
                </CardContent>
              </Card>
            )}
            <Card className="careiq-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#0A2342]">Add Fields</CardTitle></CardHeader>
              <CardContent className="space-y-1 max-h-[70vh] overflow-y-auto">
            {FIELD_TYPES.map((ft) => {
              const Icon = ft.icon;
              return (
                <button key={ft.value} onClick={() => addField(ft.value)} className="w-full flex items-center gap-2 p-2 rounded-lg text-sm text-left hover:bg-[#0E7490]/5 transition-colors text-[#0A2342]" data-testid={`add-field-${ft.value}`}>
                  <Icon className="w-4 h-4 text-[#0E7490]" />{ft.label}
                </button>
              );
            })}
            <div className="border-t border-[#E2E8F0] pt-2 mt-2">
              <p className="text-[10px] text-[#94A3B8] px-2 mb-1 font-medium uppercase tracking-wider">Presets</p>
              {PRESETS.map((preset, i) => {
                const PIcon = preset.icon;
                return (
                  <button key={i} onClick={() => addPreset(preset)} className="w-full flex items-center gap-2 p-2 rounded-lg text-sm text-left hover:bg-[#166534]/5 transition-colors text-[#166534]" data-testid={`add-preset-${i}`}>
                    <PIcon className="w-4 h-4 text-[#166534]" />{preset.label}
                  </button>
                );
              })}
            </div>
          </CardContent>
            </Card>
          </div>
        </div>

        {/* Form Canvas */}
        <div className="lg:col-span-3 space-y-4">
          <Card className="careiq-card">
            <CardContent className="p-4 space-y-3">
              <div><Label>Form Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Patient Intake Form" className="text-lg font-medium" data-testid="form-name-input" /></div>
              <div><Label>Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description of this form" data-testid="form-desc-input" /></div>
            </CardContent>
          </Card>

          {fields.length === 0 ? (
            <Card className="careiq-card">
              <CardContent className="py-10 text-center text-[#64748B] space-y-4" data-testid="empty-fields-state">
                <p>No fields yet. Click field types on the left to add them.</p>
                <div className="max-w-md mx-auto p-4 rounded-xl border border-[#0E7490]/30 bg-[#F0FDFA]">
                  <Upload className="w-6 h-6 text-[#0E7490] mx-auto mb-2" />
                  <p className="text-sm font-semibold text-[#0A2342]">Shortcut: import a paper/PDF form</p>
                  <p className="text-xs text-[#475569] mb-3">AI extracts all fields + preserves the exact layout so printed submissions match the original pixel-for-pixel.</p>
                  <Button variant="outline" className="border-[#0E7490] text-[#0E7490]" onClick={() => document.getElementById('ai-template-upload').click()} data-testid="empty-state-upload-pdf">
                    <Upload className="w-4 h-4 mr-2" />Upload PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div>
              <div className="flex flex-wrap gap-3 pr-2">
                {fields.map((field, idx) => {
                  const ftCfg = FIELD_TYPES.find(t => t.value === field.field_type);
                  const Icon = ftCfg?.icon || Type;
                  const isInfo = field.field_type === 'info';
                  const widthClass = field.width === 'half' ? 'w-full md:w-[calc(50%-6px)]' : field.width === 'third' ? 'w-full md:w-[calc(33.333%-8px)]' : 'w-full';
                  return (
                    <Card key={field.field_id} className={`careiq-card border-l-4 ${isInfo ? 'bg-[#F0FDFA]' : ''} ${widthClass}`} style={{ borderLeftColor: isInfo ? '#14B8A6' : '#0E7490' }} data-testid={`field-${idx}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex flex-col items-center gap-1 pt-1">
                            <button onClick={() => moveField(idx, -1)} className="text-[#94A3B8] hover:text-[#0A2342]"><ArrowUp className="w-3.5 h-3.5" /></button>
                            <GripVertical className="w-4 h-4 text-[#D1D5DB]" />
                            <button onClick={() => moveField(idx, 1)} className="text-[#94A3B8] hover:text-[#0A2342]"><ArrowDown className="w-3.5 h-3.5" /></button>
                          </div>
                          <div className="flex-1 space-y-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Icon className="w-4 h-4 text-[#0E7490]" />
                              <Select value={field.field_type} onValueChange={(v) => changeFieldType(idx, v)}>
                                <SelectTrigger className="h-6 w-auto min-w-[110px] text-[10px] px-2 py-0.5 bg-[#0E7490]/10 text-[#0E7490] border-[#0E7490]/20 rounded-full font-medium" data-testid={`field-type-${idx}`}>
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {FIELD_TYPES.map(t => (
                                    <SelectItem key={t.value} value={t.value} className="text-xs">
                                      {t.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {/* Width selector */}
                              <div className="flex items-center gap-1 ml-2">
                                <Columns className="w-3 h-3 text-[#94A3B8]" />
                                <Select value={field.width || 'full'} onValueChange={(v) => updateField(idx, { width: v })}>
                                  <SelectTrigger className="h-6 w-24 text-[10px] border-dashed" data-testid={`field-width-${idx}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {WIDTH_OPTIONS.map(w => <SelectItem key={w.value} value={w.value} className="text-xs">{w.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              {!isInfo && (
                                <label className="flex items-center gap-1 ml-auto text-xs text-[#64748B] cursor-pointer">
                                  <input type="checkbox" checked={field.required} onChange={(e) => updateField(idx, { required: e.target.checked })} className="rounded" />
                                  Required
                                </label>
                              )}
                            </div>
                            <div className={`grid ${isInfo ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                              <div><Label className="text-xs">Label *</Label><Input value={field.label} onChange={(e) => updateField(idx, { label: e.target.value })} placeholder="Field label" data-testid={`field-label-${idx}`} /></div>
                              {!isInfo && <div><Label className="text-xs">Placeholder</Label><Input value={field.placeholder || ''} onChange={(e) => updateField(idx, { placeholder: e.target.value })} placeholder="Placeholder text" /></div>}
                            </div>
                            {isInfo && (
                              <div>
                                <Label className="text-xs">Information Text</Label>
                                <RichTextEditor value={field.info_text || ''} onChange={(val) => updateField(idx, { info_text: val })} placeholder="Enter instructions or information to display..." />
                              </div>
                            )}
                            {(field.field_type === 'select' || field.field_type === 'radio' || field.field_type === 'checkbox' || field.field_type === 'checkbox_text') && (
                              <div>
                                <Label className="text-xs">Options (one per line)</Label>
                                <Textarea value={(field.options || []).join('\n')} onChange={(e) => updateField(idx, { options: e.target.value.split('\n') })} className="min-h-[60px] text-sm" placeholder={"Option 1\nOption 2\nOption 3"} data-testid={`field-options-${idx}`} />
                              </div>
                            )}
                            {field.field_type === 'form_reference' && (
                              <FormReferenceFieldEditor
                                field={field}
                                allForms={allForms}
                                editId={editId}
                                updateField={(patch) => updateField(idx, patch)}
                                idx={idx}
                              />
                            )}
                            {field.field_type === 'fill_blank' && (
                              <div className="space-y-2 p-3 bg-[#F0FDFA]/40 border border-[#0E7490]/20 rounded-lg" data-testid={`fib-editor-${idx}`}>
                                <Label className="text-xs font-semibold text-[#0A2342]">Template with blanks</Label>
                                <Textarea
                                  value={field.fib_template || ''}
                                  onChange={(e) => updateField(idx, { fib_template: e.target.value })}
                                  placeholder="The patient's name is ___ born on ___ weighing ___ kg."
                                  className="min-h-[70px] text-sm"
                                  data-testid={`fib-template-${idx}`}
                                />
                                <p className="text-[10px] text-[#64748B]">
                                  Use <code className="bg-white px-1 rounded">___</code> (three underscores) or <code className="bg-white px-1 rounded">{'{blank}'}</code> where each blank should appear. Detected blanks: <strong>{(field.fib_template || '').match(/(___+|\{blank\})/gi)?.length || 0}</strong>.
                                </p>
                                <div className="mt-2 p-2 bg-white border border-[#E2E8F0] rounded">
                                  <div className="text-[10px] text-[#64748B] uppercase tracking-wider mb-1">Preview</div>
                                  <FillBlankFieldBuilderPreview template={field.fib_template || ''} />
                                </div>
                              </div>
                            )}
                            {field.field_type === 'signature' && (
                              <div className="p-3 bg-[#F8FAFC] border border-dashed border-[#94A3B8] rounded-lg text-center text-xs text-[#94A3B8]">
                                <PenTool className="w-5 h-5 mx-auto mb-1" />Signature capture area
                              </div>
                            )}
                            {field.field_type === 'section' && (
                              <div className="border-b-2 border-[#0E7490] pb-1"><p className="text-[10px] text-[#94A3B8]">Section separator — visually divides form sections</p></div>
                            )}
                            {field.field_type === 'checkbox_table' && (
                              <div className="space-y-2">
                                <div><Label className="text-xs">Columns (comma-separated)</Label><Input value={(field.table_columns || []).join(', ')} onChange={(e) => updateField(idx, { table_columns: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="Item, Yes, No, N/A" data-testid={`table-cols-${idx}`} /></div>
                                <div><Label className="text-xs">Rows (one per line)</Label><Textarea value={(field.table_rows || []).join('\n')} onChange={(e) => updateField(idx, { table_rows: e.target.value.split('\n') })} className="min-h-[80px] text-sm" placeholder={"Row 1\nRow 2\nRow 3"} data-testid={`table-rows-${idx}`} /></div>
                              </div>
                            )}
                            {field.field_type === 'multi_column' && (
                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <Label className="text-xs">Columns</Label>
                                  <Select value={String(field.column_count || 2)} onValueChange={(v) => changeColumnCount(idx, Number(v))}>
                                    <SelectTrigger className="h-7 w-16 text-xs"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="2">2</SelectItem>
                                      <SelectItem value="3">3</SelectItem>
                                      <SelectItem value="4">4</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${field.column_count || 2}, 1fr)` }}>
                                  {(field.children || []).map((col, colIdx) => (
                                    <div key={colIdx} className="border border-dashed border-[#94A3B8] rounded-lg p-2 min-h-[60px] space-y-2">
                                      <div className="text-[9px] text-[#94A3B8] font-medium uppercase">Col {colIdx + 1}</div>
                                      {(col || []).map((child, childIdx) => (
                                        <div key={child.child_id} className="bg-[#F8FAFC] rounded p-2 space-y-1">
                                          <div className="flex items-center justify-between">
                                            <Select value={child.field_type} onValueChange={(v) => {
                                              const needsOpts = ['checkbox', 'radio', 'select', 'checkbox_text'];
                                              const upd = { field_type: v };
                                              if (needsOpts.includes(v) && !needsOpts.includes(child.field_type)) upd.options = ['Option 1'];
                                              if (!needsOpts.includes(v) && needsOpts.includes(child.field_type)) upd.options = null;
                                              if (v === 'info') upd.info_text = child.info_text || '';
                                              if (v !== 'info' && child.field_type === 'info') upd.info_text = null;
                                              updateChildField(idx, colIdx, childIdx, upd);
                                            }}>
                                              <SelectTrigger className="h-5 w-auto min-w-[80px] text-[9px] px-1.5 py-0 bg-[#0E7490]/10 text-[#0E7490] border-[#0E7490]/20 rounded" data-testid={`child-type-${idx}-${colIdx}-${childIdx}`}>
                                                <SelectValue placeholder="Type" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {CHILD_FIELD_TYPES.map(t => <SelectItem key={t} value={t} className="text-xs">{FIELD_TYPES.find(ft => ft.value === t)?.label || t}</SelectItem>)}
                                              </SelectContent>
                                            </Select>
                                            <button onClick={() => removeChildField(idx, colIdx, childIdx)} className="text-[#991B1B]"><Trash2 className="w-3 h-3" /></button>
                                          </div>
                                          <Input value={child.label} onChange={(e) => updateChildField(idx, colIdx, childIdx, { label: e.target.value })} placeholder="Label" className="h-6 text-[10px]" />
                                          {(child.field_type === 'checkbox' || child.field_type === 'radio' || child.field_type === 'select' || child.field_type === 'checkbox_text') && (
                                            <Textarea value={(child.options || []).join('\n')} onChange={(e) => updateChildField(idx, colIdx, childIdx, { options: e.target.value.split('\n') })} className="min-h-[40px] text-[10px]" placeholder={"Option 1\nOption 2"} />
                                          )}
                                          {child.field_type === 'info' && (
                                            <Input value={child.info_text || ''} onChange={(e) => updateChildField(idx, colIdx, childIdx, { info_text: e.target.value })} placeholder="Info text" className="h-6 text-[10px]" />
                                          )}
                                        </div>
                                      ))}
                                      <Select onValueChange={(v) => addChildField(idx, colIdx, v)}>
                                        <SelectTrigger className="h-6 text-[9px] border-dashed"><SelectValue placeholder="+ Add field..." /></SelectTrigger>
                                        <SelectContent>
                                          {CHILD_FIELD_TYPES.map(t => <SelectItem key={t} value={t} className="text-xs">{FIELD_TYPES.find(ft => ft.value === t)?.label || t}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {field.field_type === 'calculated' && (
                              <div className="space-y-2 p-3 bg-[#EFF6FF]/50 border border-[#2563EB]/20 rounded-lg" data-testid={`calc-field-${idx}`}>
                                <div className="flex items-center gap-2 text-sm text-[#1E40AF]"><Hash className="w-4 h-4" />Auto-calculated from other fields</div>
                                <div>
                                  <Label className="text-xs">Formula (use {'{field_id}'} to reference other fields)</Label>
                                  <Textarea
                                    value={field.formula || ''}
                                    onChange={(e) => updateField(idx, { formula: e.target.value })}
                                    placeholder="e.g. {weight_kg} / (({height_cm}/100) * ({height_cm}/100))"
                                    className="min-h-[60px] font-mono text-xs"
                                    data-testid={`calc-formula-${idx}`}
                                  />
                                  <FormulaHelper
                                    formula={field.formula || ''}
                                    onChange={(nf) => updateField(idx, { formula: nf })}
                                    allFields={fields}
                                    currentFieldId={field.field_id}
                                    context="field"
                                    testIdPrefix={`calc-formula-${idx}`}
                                  />
                                  <FormulaPreview formula={field.formula} format={field.format} context="field" />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <Label className="text-xs">Format</Label>
                                    <Select value={(field.format?.type) || 'number'} onValueChange={(v) => updateField(idx, { format: { ...(field.format || {}), type: v } })}>
                                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="number">Number</SelectItem>
                                        <SelectItem value="integer">Integer</SelectItem>
                                        <SelectItem value="currency">Currency ($)</SelectItem>
                                        <SelectItem value="percent">Percent (%)</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className="text-xs">Decimals</Label>
                                    <Input
                                      type="number" min="0" max="6"
                                      value={field.format?.decimals ?? (field.field_type === 'calc_table' ? 0 : 2)}
                                      onChange={(e) => updateField(idx, { format: { ...(field.format || {}), decimals: parseInt(e.target.value) } })}
                                      className="h-8 text-xs"
                                      data-testid={`calc-decimals-${idx}`}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                            {field.field_type === 'calc_table' && (
                              <CalcTableEditor field={field} updateField={(upd) => updateField(idx, upd)} idx={idx} allFields={fields} />
                            )}
                            {field.field_type === 'reminder' && (
                              <div className="space-y-2 p-3 bg-[#FEF3C7]/50 border border-[#CA8A04]/20 rounded-lg">
                                <div className="flex items-center gap-2 text-sm text-[#92400E]"><Clock className="w-4 h-4" />Creates a follow-up task after submission</div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div><Label className="text-xs">Days After Submission</Label><Input type="number" min="1" value={field.reminder_days || 7} onChange={(e) => updateField(idx, { reminder_days: parseInt(e.target.value) || 7 })} data-testid={`reminder-days-${idx}`} /></div>
                                  <div><Label className="text-xs">Reminder Label (optional)</Label><Input value={field.reminder_label || ''} onChange={(e) => updateField(idx, { reminder_label: e.target.value })} placeholder="e.g. 30-day follow-up" data-testid={`reminder-label-${idx}`} /></div>
                                </div>
                              </div>
                            )}
                            {/* Patient field reference */}
                            {field.field_type !== 'multi_column' && (
                              <PatientFieldRefPicker
                                value={field.patient_field_ref}
                                onChange={(key) => updateField(idx, { patient_field_ref: key })}
                                testIdPrefix={`patient-field-ref-${idx}`}
                              />
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            <button onClick={() => duplicateField(idx)} className="text-[#0E7490] hover:text-[#0C6580] p-1" title="Duplicate field" data-testid={`duplicate-field-${idx}`}><Copy className="w-4 h-4" /></button>
                            <button onClick={() => removeField(idx)} className="text-[#991B1B] hover:text-[#7F1D1D] p-1" data-testid={`remove-field-${idx}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Print Layout Tab */}
      {activeTab === 'print' && (
        <div className="space-y-6" data-testid="print-layout-tab">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Template Settings */}
            <Card className="careiq-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Page Settings</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Page Size</Label>
                    <Select value={printTemplate.page_size} onValueChange={v => setPrintTemplate(p => ({ ...p, page_size: v }))}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="letter">Letter</SelectItem><SelectItem value="a4">A4</SelectItem><SelectItem value="legal">Legal</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Orientation</Label>
                    <Select value={printTemplate.orientation} onValueChange={v => setPrintTemplate(p => ({ ...p, orientation: v }))}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="portrait">Portrait</SelectItem><SelectItem value="landscape">Landscape</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Margins (mm)</Label>
                  <div className="grid grid-cols-4 gap-1">
                    {['top','right','bottom','left'].map(side => (
                      <div key={side}><Label className="text-[9px] text-[#94A3B8]">{side}</Label>
                        <Input type="number" className="h-7 text-xs" value={printTemplate.margins[side]} onChange={e => setPrintTemplate(p => ({ ...p, margins: { ...p.margins, [side]: parseInt(e.target.value) || 0 } }))} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-3">
                  <Label className="text-xs font-semibold">Header</Label>
                  <div className="space-y-2 mt-1">
                    <Input value={printTemplate.header.text} onChange={e => setPrintTemplate(p => ({ ...p, header: { ...p.header, text: e.target.value } }))} placeholder="Header text (leave empty for form name)" className="h-7 text-xs" />
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={printTemplate.header.show_logo} onChange={e => setPrintTemplate(p => ({ ...p, header: { ...p.header, show_logo: e.target.checked } }))} className="rounded" />Show practice logo</label>
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={printTemplate.header.show_org_name} onChange={e => setPrintTemplate(p => ({ ...p, header: { ...p.header, show_org_name: e.target.checked } }))} className="rounded" />Show org name</label>
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={printTemplate.header.show_org_address} onChange={e => setPrintTemplate(p => ({ ...p, header: { ...p.header, show_org_address: e.target.checked } }))} className="rounded" />Show address</label>
                  </div>
                </div>

                <div className="border-t pt-3">
                  <Label className="text-xs font-semibold">Patient Info</Label>
                  <label className="flex items-center gap-2 text-xs mt-1"><input type="checkbox" checked={printTemplate.patient_info.show} onChange={e => setPrintTemplate(p => ({ ...p, patient_info: { ...p.patient_info, show: e.target.checked } }))} className="rounded" />Show patient section</label>
                  <div className="mt-1"><Label className="text-[9px] text-[#94A3B8]">Columns</Label>
                    <Select value={String(printTemplate.patient_info.columns)} onValueChange={v => setPrintTemplate(p => ({ ...p, patient_info: { ...p.patient_info, columns: parseInt(v) } }))}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="2">2 columns</SelectItem><SelectItem value="3">3 columns</SelectItem><SelectItem value="4">4 columns</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="border-t pt-3">
                  <Label className="text-xs font-semibold">Footer</Label>
                  <Input value={printTemplate.footer.text} onChange={e => setPrintTemplate(p => ({ ...p, footer: { ...p.footer, text: e.target.value } }))} placeholder="Footer text" className="h-7 text-xs mt-1" />
                  <label className="flex items-center gap-2 text-xs mt-1"><input type="checkbox" checked={printTemplate.footer.show_page_numbers} onChange={e => setPrintTemplate(p => ({ ...p, footer: { ...p.footer, show_page_numbers: e.target.checked } }))} className="rounded" />Page numbers</label>
                </div>

                <Button className="careiq-btn-primary w-full" onClick={savePrintTemplate} data-testid="save-print-template-btn"><Save className="w-3 h-3 mr-2" />Save Template</Button>
              </CardContent>
            </Card>

            {/* Preview */}
            <div className="lg:col-span-2">
              <Card className="careiq-card">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Print Preview</CardTitle></CardHeader>
                <CardContent>
                  <div className="bg-white border-2 border-[#E2E8F0] rounded-lg shadow-inner mx-auto" style={{ maxWidth: printTemplate.orientation === 'landscape' ? '800px' : '600px', padding: `${printTemplate.margins.top}px ${printTemplate.margins.right}px ${printTemplate.margins.bottom}px ${printTemplate.margins.left}px`, minHeight: '500px' }}>
                    {/* Header preview */}
                    <div className="border-b-2 border-[#0E7490] pb-2 mb-3" style={{ textAlign: printTemplate.header.alignment }}>
                      {printTemplate.header.show_logo && <div className="w-10 h-10 bg-[#0E7490]/10 rounded inline-flex items-center justify-center text-[#0E7490] text-xs mb-1">Logo</div>}
                      {printTemplate.header.show_org_name && <div className="text-sm font-bold text-[#0A2342]">[Organization Name]</div>}
                      {printTemplate.header.show_org_address && <div className="text-[9px] text-[#64748B]">[Address]</div>}
                      <div style={{ fontSize: `${printTemplate.header.font_size || 16}px` }} className="font-bold text-[#0A2342] mt-1">{printTemplate.header.text || name || 'Form Title'}</div>
                    </div>
                    {/* Patient info preview */}
                    {printTemplate.patient_info.show && (
                      <div className="border border-[#E2E8F0] rounded p-2 mb-3">
                        <div className="text-[9px] font-bold text-[#64748B] mb-1">PATIENT INFORMATION</div>
                        <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${printTemplate.patient_info.columns}, 1fr)` }}>
                          {printTemplate.patient_info.fields.map(f => (
                            <div key={f} className="text-[8px]"><span className="text-[#94A3B8]">{f}: </span><span className="border-b border-[#C0C0C0] inline-block min-w-[60px]">&nbsp;</span></div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Fields preview */}
                    <div className="space-y-2">
                      {fields.map((f, i) => {
                        if (f.field_type === 'section') return <div key={i} className="border-b border-[#0E7490] pt-2 pb-0.5 text-xs font-bold text-[#0E7490]">{f.label}</div>;
                        if (f.field_type === 'info') return <div key={i} className="text-[8px] text-[#64748B] bg-[#F8FAFC] p-1 rounded" dangerouslySetInnerHTML={{ __html: f.info_text || f.label }} />;
                        if (f.field_type === 'reminder') return null;
                        if (f.field_type === 'signature') return <div key={i} className="mt-4"><div className="border-b border-black w-48 mt-6" /><div className="text-[8px] text-[#64748B]">{f.label}</div></div>;
                        const w = f.width === 'half' ? 'inline-block w-[48%] mr-[2%] align-top' : f.width === 'third' ? 'inline-block w-[31%] mr-[2%] align-top' : 'block';
                        return <div key={i} className={`${w} mb-1`}><div className="text-[8px] text-[#64748B]">{f.label}{f.required ? ' *' : ''}</div><div className="border-b border-[#C0C0C0] min-h-[14px]" /></div>;
                      })}
                    </div>
                    {/* Footer preview */}
                    <div className="border-t border-[#E2E8F0] mt-4 pt-1 text-[7px] text-[#94A3B8] flex justify-between">
                      <span>{printTemplate.footer.text}</span>
                      {printTemplate.footer.show_page_numbers && <span>Page 1</span>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-md" data-testid="form-settings-dialog">
          <DialogHeader><DialogTitle>Form Settings</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="form-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Remind to update every (days)</Label>
              <Input type="number" min="1" max="730" value={updateIntervalDays} onChange={e => setUpdateIntervalDays(e.target.value)} placeholder="e.g. 60 for CMS-485 — leave blank for no reminder" data-testid="form-update-interval-input" />
              <p className="text-[11px] text-[#64748B] mt-1">After each submission the patient's record gets a next-update date; staff can change it when saving.</p>
            </div>
            <div>
              <Label>Assign to Roles</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ROLES.map(role => (
                  <button key={role} onClick={() => toggleRole(role)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${assignedRoles.includes(role) ? 'bg-[#0E7490] text-white border-[#0E7490]' : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#0E7490]'}`} data-testid={`role-toggle-${role}`}>
                    {ROLE_LABELS[role]}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-1">Empty = visible to admins only</p>
            </div>
            <div className="pt-3 border-t border-[#E2E8F0]">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={addAsPatientTab}
                  onChange={(e) => setAddAsPatientTab(e.target.checked)}
                  disabled={tabAlreadyLinked}
                  className="mt-1"
                  data-testid="add-as-patient-tab"
                />
                <div>
                  <div className="text-sm font-medium text-[#0A2342]">Add as patient-record tab</div>
                  <div className="text-[11px] text-[#64748B]">
                    {tabAlreadyLinked
                      ? 'This form is already linked as a patient tab.'
                      : 'Saves this form as a new tab on every patient record. Nurses/admins can fill it inline on mobile or desktop.'}
                  </div>
                </div>
              </label>
            </div>
            <Button variant="outline" className="w-full" onClick={() => setShowSettings(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Trail Dialog */}
      <Dialog open={showAudit} onOpenChange={setShowAudit}>
        <DialogContent className="sm:max-w-xl" data-testid="form-audit-dialog">
          <DialogHeader><DialogTitle>Form Audit Trail</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[400px] mt-2">
            {loadingAudit ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#0E7490] animate-spin" /></div>
            ) : auditEntries.length > 0 ? (
              <div className="space-y-3">
                {auditEntries.map((entry) => (
                  <div key={entry.id} className="border border-[#E2E8F0] rounded-lg p-3" data-testid={`audit-entry-${entry.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-[#0A2342]">{entry.changed_by_name || 'Unknown'}</span>
                      <span className="text-xs text-[#64748B]">{new Date(entry.timestamp).toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-[#64748B]">
                      <span className="bg-[#0E7490]/10 text-[#0E7490] px-1.5 py-0.5 rounded">{entry.action}</span>
                      <span className="ml-2">v{entry.version_before} → v{entry.version_after}</span>
                    </div>
                    {entry.changes && Object.keys(entry.changes).length > 0 && (
                      <div className="mt-2 text-xs text-[#64748B] space-y-0.5">
                        {Object.entries(entry.changes).map(([key, val]) => (
                          <div key={key}><b>{key}:</b> {typeof val === 'object' ? JSON.stringify(val) : String(val)}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#64748B]">No audit entries yet</div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Form Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh]" data-testid="form-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-[#0E7490]" />Form Preview</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] mt-2">
            <div className="p-4 border border-[#E2E8F0] rounded-xl bg-white">
              <h2 className="text-xl font-semibold text-[#0A2342] mb-1">{name || 'Untitled Form'}</h2>
              {description && <p className="text-sm text-[#64748B] mb-4">{description}</p>}
              <div className="flex flex-wrap gap-4">
                {fields.length === 0 ? (
                  <p className="text-sm text-[#94A3B8] py-8 w-full text-center">No fields added yet</p>
                ) : (
                  fields.map((field, idx) => {
                    const widthClass = field.width === 'half' ? 'w-full md:w-[calc(50%-8px)]' : field.width === 'third' ? 'w-full md:w-[calc(33.333%-11px)]' : 'w-full';
                    const isSection = field.field_type === 'section';
                    const isInfo = field.field_type === 'info';
                    const isSig = field.field_type === 'signature';
                    const isTable = field.field_type === 'checkbox_table';
                    const isMultiCol = field.field_type === 'multi_column';

                    if (isSection) {
                      return <div key={field.field_id} className="w-full border-b-2 border-[#0E7490] pb-1 pt-3"><span className="text-sm font-semibold text-[#0E7490]">{field.label}</span></div>;
                    }
                    if (isMultiCol) {
                      return (
                        <div key={field.field_id} className="w-full">
                          {field.label && <label className="text-sm font-medium text-[#0A2342] mb-2 block">{field.label}</label>}
                          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${field.column_count || 2}, 1fr)` }}>
                            {(field.children || []).map((col, colIdx) => (
                              <div key={colIdx} className="space-y-3">
                                {(col || []).map((child, ci) => (
                                  <div key={ci} className="space-y-1">
                                    {child.label && <label className="text-sm font-medium text-[#0A2342]">{child.label}</label>}
                                    {(child.field_type === 'text' || child.field_type === 'number' || child.field_type === 'email' || child.field_type === 'phone' || child.field_type === 'date') && <input type="text" disabled placeholder={child.placeholder} className="w-full h-9 px-3 border border-[#E2E8F0] rounded-md text-sm bg-[#F8FAFC]" />}
                                    {child.field_type === 'textarea' && <textarea disabled className="w-full min-h-[50px] px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-[#F8FAFC]" />}
                                    {child.field_type === 'checkbox' && <div className="space-y-1">{(child.options || []).map((o, oi) => <label key={oi} className="flex items-center gap-2 text-sm"><input type="checkbox" disabled className="rounded" />{o}</label>)}</div>}
                                    {child.field_type === 'radio' && <div className="space-y-1">{(child.options || []).map((o, oi) => <label key={oi} className="flex items-center gap-2 text-sm"><input type="radio" disabled />{o}</label>)}</div>}
                                    {child.field_type === 'select' && <select disabled className="w-full h-9 px-3 border border-[#E2E8F0] rounded-md text-sm bg-[#F8FAFC]"><option>{child.placeholder || 'Select...'}</option></select>}
                                    {child.field_type === 'info' && <div className="p-2 bg-[#F0FDFA] rounded text-xs text-[#0A2342]">{child.info_text || child.label}</div>}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={field.field_id} className={`${widthClass}`}>
                        {!isInfo && !isSig && !isTable && (
                          <div className="space-y-1">
                            <label className="text-sm font-medium text-[#0A2342]">{field.label}{field.required && <span className="text-[#DC2626] ml-0.5">*</span>}</label>
                            {(field.field_type === 'text' || field.field_type === 'number' || field.field_type === 'email' || field.field_type === 'phone' || field.field_type === 'date') && (
                              <input type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'} placeholder={field.placeholder} disabled className="w-full h-9 px-3 border border-[#E2E8F0] rounded-md text-sm bg-[#F8FAFC] text-[#94A3B8]" />
                            )}
                            {field.field_type === 'textarea' && <textarea disabled placeholder={field.placeholder} className="w-full min-h-[60px] px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-[#F8FAFC]" />}
                            {field.field_type === 'select' && (
                              <select disabled className="w-full h-9 px-3 border border-[#E2E8F0] rounded-md text-sm bg-[#F8FAFC] text-[#94A3B8]">
                                <option>{field.placeholder || 'Select...'}</option>
                                {(field.options || []).map((o, i) => <option key={i}>{o}</option>)}
                              </select>
                            )}
                            {field.field_type === 'radio' && (
                              <div className="space-y-1">{(field.options || []).map((o, i) => <label key={i} className="flex items-center gap-2 text-sm text-[#0A2342]"><input type="radio" disabled name={`preview-${field.field_id}`} />{o}</label>)}</div>
                            )}
                            {field.field_type === 'checkbox' && (
                              <div className="space-y-1">{(field.options || []).map((o, i) => <label key={i} className="flex items-center gap-2 text-sm text-[#0A2342]"><input type="checkbox" disabled className="rounded" />{o}</label>)}</div>
                            )}
                            {field.field_type === 'checkbox_text' && (
                              <div className="space-y-1.5">{(field.options || []).map((o, i) => <div key={i} className="flex items-center gap-2"><input type="checkbox" disabled className="rounded shrink-0" /><span className="text-sm text-[#0A2342] shrink-0">{o}</span><input type="text" disabled placeholder="Details..." className="flex-1 h-7 px-2 border border-[#E2E8F0] rounded text-sm bg-[#F8FAFC]" /></div>)}</div>
                            )}
                            {field.field_type === 'file' && <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-4 text-center text-xs text-[#94A3B8]"><Upload className="w-5 h-5 mx-auto mb-1" />Click to upload</div>}
                            {field.field_type === 'form_reference' && <div className="p-2 bg-[#0E7490]/5 rounded text-xs text-[#0E7490]">References another form</div>}
                          </div>
                        )}
                        {isInfo && (
                          <div className="p-3 bg-[#F0FDFA] border border-[#14B8A6]/20 rounded-lg text-sm text-[#0A2342] flex items-start gap-2"><Info className="w-4 h-4 text-[#14B8A6] mt-0.5 shrink-0" /><span>{field.info_text || field.label}</span></div>
                        )}
                        {isSig && (
                          <div className="space-y-1">
                            <label className="text-sm font-medium text-[#0A2342]">{field.label}</label>
                            <div className="border-2 border-dashed border-[#94A3B8] rounded-lg p-6 text-center text-xs text-[#94A3B8]"><PenTool className="w-5 h-5 mx-auto mb-1" />Click to sign</div>
                          </div>
                        )}
                        {isTable && (
                          <div className="space-y-1">
                            <label className="text-sm font-medium text-[#0A2342]">{field.label}</label>
                            <table className="w-full text-sm border border-[#E2E8F0] rounded">
                              <thead><tr className="bg-[#F1F5F9]">{(field.table_columns || ['Item']).map((c, ci) => <th key={ci} className="p-2 text-left text-xs font-medium text-[#64748B] border-b">{c}</th>)}</tr></thead>
                              <tbody>{(field.table_rows || ['Row 1']).map((r, ri) => (
                                <tr key={ri} className="border-b"><td className="p-2 text-xs">{r}</td>{(field.table_columns || []).slice(1).map((_, ci) => <td key={ci} className="p-2 text-center"><input type="checkbox" disabled className="rounded" /></td>)}</tr>
                              ))}</tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------- Calc Table Editor sub-component ----------------
function CalcTableEditor({ field, updateField, idx, allFields = [] }) {
  const cfg = field.calc_table_config || { columns: [], totals: { enabled: false, cells: {} } };
  const updCfg = (patch) => updateField({ calc_table_config: { ...cfg, ...patch } });
  const updCols = (cols) => updCfg({ columns: cols });

  const addColumn = () => {
    const key = `col${(cfg.columns || []).length + 1}`;
    updCols([...(cfg.columns || []), { key, label: 'New Column', type: 'number' }]);
  };
  const delColumn = (i) => updCols(cfg.columns.filter((_, x) => x !== i));
  const updateCol = (i, patch) => updCols(cfg.columns.map((c, x) => x === i ? { ...c, ...patch } : c));

  return (
    <div className="space-y-3 p-3 bg-[#F5F3FF]/50 border border-[#7C3AED]/20 rounded-lg" data-testid={`calc-table-${idx}`}>
      <div className="flex items-center gap-2 text-sm text-[#6D28D9]"><Table2 className="w-4 h-4" />Spreadsheet-style table with auto-calculating cells</div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold">Columns</Label>
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addColumn} data-testid={`add-calc-col-${idx}`}>+ Add Column</Button>
        </div>
        {(cfg.columns || []).map((col, ci) => (
          <div key={ci} className="bg-white rounded p-2 border border-[#E2E8F0] space-y-1.5" data-testid={`calc-col-${idx}-${ci}`}>
            <div className="flex gap-2 items-center">
              <Input value={col.label} onChange={(e) => updateCol(ci, { label: e.target.value })} placeholder="Column label" className="h-7 text-xs flex-1" />
              <Input value={col.key} onChange={(e) => updateCol(ci, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })} placeholder="key" className="h-7 text-xs w-24 font-mono" />
              <Select value={col.type} onValueChange={(v) => updateCol(ci, { type: v, formula: v === 'formula' ? (col.formula || '') : undefined })}>
                <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="formula">Formula</SelectItem>
                </SelectContent>
              </Select>
              <button type="button" onClick={() => delColumn(ci)} className="text-[#991B1B] hover:text-[#7F1D1D]"><Trash2 className="w-4 h-4" /></button>
            </div>
            {col.type === 'formula' && (
              <>
                <Input
                  value={col.formula || ''}
                  onChange={(e) => updateCol(ci, { formula: e.target.value })}
                  placeholder="e.g. row.qty * row.price"
                  className="h-7 text-xs font-mono"
                  data-testid={`calc-col-formula-${idx}-${ci}`}
                />
                <FormulaHelper
                  formula={col.formula || ''}
                  onChange={(nf) => updateCol(ci, { formula: nf })}
                  allFields={allFields}
                  currentFieldId={field.field_id}
                  context="row"
                  testIdPrefix={`calc-col-formula-${idx}-${ci}`}
                />
                <FormulaPreview formula={col.formula} format={col.format || field.format} context="row" />
              </>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Initial Rows</Label>
          <Input type="number" min="1" max="50" value={cfg.initial_rows || 3} onChange={(e) => updCfg({ initial_rows: Math.max(1, parseInt(e.target.value) || 1) })} className="h-8 text-xs" />
        </div>
        <div className="flex items-end gap-2 pb-1">
          <input type="checkbox" checked={!!cfg.allow_add_rows} onChange={(e) => updCfg({ allow_add_rows: e.target.checked })} />
          <Label className="text-xs">Allow user to add rows</Label>
        </div>
      </div>

      <CalcTableDefaultRows cfg={cfg} updCfg={updCfg} idx={idx} />

      <div className="border-t border-[#E2E8F0] pt-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!cfg.totals?.enabled} onChange={(e) => updCfg({ totals: { ...(cfg.totals || {}), enabled: e.target.checked, cells: cfg.totals?.cells || {} } })} data-testid={`calc-totals-toggle-${idx}`} />
          <span className="text-xs font-semibold">Show totals row</span>
        </label>
        {cfg.totals?.enabled && (
          <div className="mt-2 space-y-1">
            {(cfg.columns || []).filter(c => c.type !== 'text').map(col => {
              const cell = cfg.totals.cells?.[col.key] || { agg: '' };
              const agg = typeof cell === 'string' ? cell : cell.agg;
              const cellFormula = (typeof cell === 'object' && cell.formula) || '';
              return (
                <div key={col.key} className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-24 text-[#475569] truncate">{col.label}</span>
                    <Select value={agg || 'none'} onValueChange={(v) => {
                      const cells = { ...(cfg.totals.cells || {}) };
                      if (v === 'none') delete cells[col.key];
                      else if (v === 'formula') cells[col.key] = { agg: 'formula', formula: cellFormula || '' };
                      else cells[col.key] = { agg: v };
                      updCfg({ totals: { ...cfg.totals, cells } });
                    }}>
                      <SelectTrigger className="h-7 text-xs w-32" data-testid={`calc-totals-agg-${idx}-${col.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        <SelectItem value="sum">Sum</SelectItem>
                        <SelectItem value="avg">Average</SelectItem>
                        <SelectItem value="count">Count</SelectItem>
                        <SelectItem value="min">Min</SelectItem>
                        <SelectItem value="max">Max</SelectItem>
                        <SelectItem value="formula">Custom formula…</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {agg === 'formula' && (
                    <div className="ml-26 space-y-1 pl-2 border-l-2 border-[#7C3AED]/30">
                      <Input
                        value={cellFormula}
                        onChange={(e) => {
                          const cells = { ...(cfg.totals.cells || {}) };
                          cells[col.key] = { agg: 'formula', formula: e.target.value };
                          updCfg({ totals: { ...cfg.totals, cells } });
                        }}
                        placeholder="e.g. col.qty.sum * {tax_rate}"
                        className="h-7 text-xs font-mono"
                        data-testid={`calc-totals-formula-${idx}-${col.key}`}
                      />
                      <FormulaHelper
                        formula={cellFormula}
                        onChange={(nf) => {
                          const cells = { ...(cfg.totals.cells || {}) };
                          cells[col.key] = { agg: 'formula', formula: nf };
                          updCfg({ totals: { ...cfg.totals, cells } });
                        }}
                        allFields={allFields}
                        currentFieldId={field.field_id}
                        context="row"
                        testIdPrefix={`calc-totals-formula-${idx}-${col.key}`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------- Calc Table Default Rows editor ----------
function CalcTableDefaultRows({ cfg, updCfg, idx }) {
  const cols = (cfg.columns || []).filter(c => c.type !== 'formula'); // formulas auto-compute
  const rows = cfg.default_rows || [];
  const setRows = (next) => updCfg({ default_rows: next });

  const addRow = () => setRows([...rows, Object.fromEntries(cols.map(c => [c.key, '']))]);
  const removeRow = (i) => setRows(rows.filter((_, x) => x !== i));
  const updateCell = (rIdx, colKey, val) => setRows(rows.map((r, i) => i === rIdx ? { ...r, [colKey]: val } : r));

  if (cols.length === 0) return null;

  return (
    <div className="border-t border-[#E2E8F0] pt-2" data-testid={`calc-defaults-${idx}`}>
      <div className="flex items-center justify-between mb-1">
        <div>
          <Label className="text-xs font-semibold">Pre-filled Rows (optional)</Label>
          <p className="text-[10px] text-[#64748B]">Each new submission starts with these rows already filled in. Formula cells re-compute automatically.</p>
        </div>
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addRow} data-testid={`add-default-row-${idx}`}>+ Default Row</Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-[10px] text-[#94A3B8] italic py-1">No pre-filled rows. Initial Rows ({cfg.initial_rows || 3}) start blank.</div>
      ) : (
        <div className="space-y-1">
          {rows.map((row, ri) => (
            <div key={ri} className="flex items-center gap-1 bg-white rounded border border-[#E2E8F0] p-1" data-testid={`default-row-${idx}-${ri}`}>
              {cols.map(c => (
                <Input
                  key={c.key}
                  value={row[c.key] ?? ''}
                  onChange={(e) => updateCell(ri, c.key, c.type === 'number' && e.target.value !== '' ? Number(e.target.value) : e.target.value)}
                  type={c.type === 'number' ? 'number' : 'text'}
                  placeholder={c.label}
                  className="h-7 text-xs flex-1 min-w-0"
                  data-testid={`default-cell-${idx}-${ri}-${c.key}`}
                />
              ))}
              <button type="button" onClick={() => removeRow(ri)} className="text-[#991B1B] hover:text-[#7F1D1D] px-1" aria-label="Remove default row"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Form Reference field editor ----------
// Picker for `form_reference` fields: select the target form, then select the
// specific referenced field from a dropdown populated by the target form's
// fields (no more hand-typing field IDs).
function FormReferenceFieldEditor({ field, allForms, editId, updateField, idx }) {
  const [refFields, setRefFields] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!field.reference_form_id) { setRefFields([]); return; }
    let alive = true;
    setLoading(true);
    _formsApiForRef.get(field.reference_form_id)
      .then(res => {
        if (!alive) return;
        const f = (res.data?.fields || []).filter(x =>
          !['section', 'info', 'reminder'].includes(x.field_type)
        );
        setRefFields(f);
      })
      .catch(() => { if (alive) setRefFields([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [field.reference_form_id]);

  return (
    <div className="grid grid-cols-2 gap-3" data-testid={`form-ref-editor-${idx}`}>
      <div>
        <Label className="text-xs">Reference Form</Label>
        <Select
          value={field.reference_form_id || ''}
          onValueChange={(v) => updateField({ reference_form_id: v, reference_field_id: null })}
        >
          <SelectTrigger data-testid={`ref-form-select-${idx}`}><SelectValue placeholder="Select form" /></SelectTrigger>
          <SelectContent>
            {allForms.filter(f => f.id !== editId).map(f => (
              <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Reference Field (optional)</Label>
        {field.reference_form_id ? (
          <Select
            value={field.reference_field_id || '__none__'}
            onValueChange={(v) => updateField({ reference_field_id: v === '__none__' ? null : v })}
          >
            <SelectTrigger data-testid={`ref-field-select-${idx}`}>
              <SelectValue placeholder={loading ? 'Loading…' : 'Select a field'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Entire form —</SelectItem>
              {refFields.map(f => (
                <SelectItem key={f.field_id} value={f.field_id}>
                  {f.label || f.field_id} <span className="text-[10px] text-[#94A3B8]">· {f.field_type}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="text-[11px] text-[#94A3B8] italic pt-2">Select a reference form first</div>
        )}
      </div>
    </div>
  );
}

// ---------- Fill-in-the-blank preview (non-interactive) ----------
function FillBlankFieldBuilderPreview({ template }) {
  return <FillBlankField template={template} value={[]} onChange={() => {}} readOnly />;
}

